/**
 * creneaux.ts — le taux de commission PAR CRÉNEAU horaire, calcul PUR.
 *
 * Décision de Kayla (30-09-2026) : « 15 %, ce n'est pas les chatteurs qui ont
 * ce shift, ce sont les ventes réalisées dans ce shift. » Une vente dont
 * l'heure de création (heure murale Europe/Paris) tombe dans un créneau payé
 * est payée au % du créneau ; les autres au % de base du chatteur
 * (`accountMembers.salesCommissionPercent`). Le créneau REMPLACE la base, même
 * plus bas ; deux créneaux qui se chevauchent : le plus haut l'emporte.
 *
 * Les créneaux vivent dans Convex (`shiftCommissionRates`) : minutes depuis
 * minuit en heure murale de Paris, multiples de 30, `endMinute <= startMinute`
 * ⇒ le créneau traverse minuit. Le backend range chaque vente dans l'une des
 * 48 demi-heures de la journée (`gross_by_half_hour`, index i = minute i×30).
 *
 * ⛔ Ce calcul existe en DEUX exemplaires — ici (le « dû » affiché) et dans le
 * backend Cyrano (le retrait, `payroll/commission.py`). Ils partagent la même
 * table de cas (`CAS_DE_CRENEAU` dans `__tests__/creneaux.test.ts`) : si l'une
 * bouge, l'autre doit bouger, sinon le Portefeuille annonce un montant que le
 * retrait ne verse pas.
 *
 * Fonctions pures (0 dépendance React/réseau).
 */
import { NET_REVENUE_MULTIPLIER } from "./commission";

/** Un créneau payé, tel que rendu par `shiftCommissionRates.listForAccount`. */
export type CommissionSlot = {
  /** Minutes depuis minuit (heure murale de Paris), multiple de 30. */
  startMinute: number;
  /** Idem. `<= startMinute` ⇒ traverse minuit. */
  endMinute: number;
  /** 0-100. */
  percent: number;
};

/** Nombre de demi-heures dans une journée. */
export const HALF_HOURS_PER_DAY = 48;

/** `minute` (0..1439) est-elle dans le créneau ? Début inclus, fin exclue. */
export function slotContains(slot: CommissionSlot, minute: number): boolean {
  const { startMinute: start, endMinute: end } = slot;
  if (start < end) return start <= minute && minute < end;
  return minute >= start || minute < end;
}

/**
 * Le % qui s'applique à une vente faite à `minute` : le MAX des créneaux qui la
 * contiennent ; aucun ⇒ la base. Le créneau remplace la base, même plus bas.
 */
export function effectivePercent(
  minute: number,
  basePercent: number,
  slots: readonly CommissionSlot[],
): number {
  let best: number | null = null;
  for (const s of slots) {
    if (!slotContains(s, minute)) continue;
    if (best === null || s.percent > best) best = s.percent;
  }
  return best ?? basePercent;
}

/**
 * Le total ET la part issue des créneaux, en une seule passe (le total est
 * exactement celui de `commissionFromHalfHourBuckets`).
 */
export function splitHalfHourBuckets(
  buckets: readonly number[],
  basePercent: number,
  slots: readonly CommissionSlot[],
): { totalCents: number; slotCents: number } {
  if (buckets.length !== HALF_HOURS_PER_DAY) {
    throw new Error(
      `buckets: ${HALF_HOURS_PER_DAY} demi-heures attendues, ${buckets.length} reçues`,
    );
  }
  // Même suite d'opérations flottantes que le retrait
  // (`payroll/creneaux.py::commission_par_demi_heure`) : on additionne d'abord
  // les bruts ENTIERS qui partagent un taux (dans l'ordre de leur première
  // demi-heure non vide), puis une multiplication par taux. Additionner 48
  // petits produits pourrait, près d'un demi-cent, arrondir de l'autre côté.
  const brutParTaux = new Map<number, number>();
  let slotCents = 0;
  for (let i = 0; i < HALF_HOURS_PER_DAY; i++) {
    const brut = buckets[i]!;
    if (!brut) continue;
    const minute = i * 30;
    const pct = effectivePercent(minute, basePercent, slots);
    brutParTaux.set(pct, (brutParTaux.get(pct) ?? 0) + brut);
    if (slots.some((s) => slotContains(s, minute))) {
      slotCents += (brut * NET_REVENUE_MULTIPLIER * pct) / 100;
    }
  }
  let totalCents = 0;
  for (const [pct, brut] of brutParTaux) {
    totalCents += brut * NET_REVENUE_MULTIPLIER * pct / 100;
  }
  return { totalCents, slotCents };
}

/**
 * Commission (cents USD, FLOAT — l'appelant arrondit au total) sur un brut
 * rangé par demi-heure : Σ brut[i] × NET_REVENUE_MULTIPLIER × % effectif(i×30).
 *
 * `null` quand le % de base est inconnu : un membre sans % de base ne touche
 * rien sur ses ventes propres, même dans un créneau. Comme
 * `commissionFromGrossCents`, l'appelant affiche « — », jamais « $0 ».
 * Lève si `buckets` n'a pas 48 cases : un tableau tronqué ne se devine pas.
 */
export function commissionFromHalfHourBuckets(
  buckets: readonly number[],
  basePercent: number | null | undefined,
  slots: readonly CommissionSlot[],
): number | null {
  if (buckets.length !== HALF_HOURS_PER_DAY) {
    throw new Error(
      `buckets: ${HALF_HOURS_PER_DAY} demi-heures attendues, ${buckets.length} reçues`,
    );
  }
  if (basePercent == null || !Number.isFinite(basePercent)) return null;
  return splitHalfHourBuckets(buckets, basePercent, slots).totalCents;
}

/**
 * Les 48 demi-heures recollent-elles au brut ? Le backend tronque CHAQUE
 * demi-heure au cent (comme `int(...)` sur le brut total), donc leur somme
 * peut être sous le brut de moins d'un cent par case — jamais au-dessus. Le
 * retrait (`payroll/commission.py`) calcule sur ces MÊMES cases tronquées :
 * les accepter garde l'écran et le versement identiques. Une égalité stricte
 * aurait affiché « — » à tort dès qu'un taux de change donne une fraction de
 * cent. Tolérance = une case par ligne additionnée, bornée large (4 lignes).
 */
export function bucketsRecollent(
  buckets: readonly number[],
  grossCents: number,
): boolean {
  if (buckets.length !== HALF_HOURS_PER_DAY) return false;
  const ecart = grossCents - buckets.reduce((a, b) => a + b, 0);
  return ecart >= 0 && ecart < HALF_HOURS_PER_DAY * 4;
}

/**
 * La commission d'UN brut, créneaux compris (cents, FLOAT — l'appelant
 * arrondit). Pour les écrans qui ne passent pas par `computeMemberOwed` : la
 * pastille « Net », « Gagné », le cumul du shift, la courbe du Portefeuille,
 * la fiche d'un membre dans Team.
 *
 * `null` — donc « — », jamais un faux montant — quand :
 *   - le % de base est inconnu (comme `commissionFromGrossCents`) ;
 *   - les créneaux ne sont pas encore chargés (`slots` undefined) : sans eux
 *     on ne sait pas si la base s'applique ;
 *   - il existe des créneaux et les demi-heures manquent (backend d'avant) ou
 *     ne recollent pas au brut. Un brut nul sans demi-heures vaut 0 : il n'y a
 *     rien à ranger.
 * Sans créneau : brut × NET_REVENUE_MULTIPLIER × base, comme avant.
 */
export function commissionOfGross(args: {
  grossCents: number;
  /** 48 entiers (`gross_by_half_hour` du backend). Absent = backend d'avant. */
  halfHours: readonly number[] | null | undefined;
  basePercent: number | null | undefined;
  slots: readonly CommissionSlot[] | undefined;
}): number | null {
  const { grossCents, halfHours, basePercent, slots } = args;
  if (basePercent == null || !Number.isFinite(basePercent)) return null;
  if (!Number.isFinite(grossCents) || slots === undefined) return null;
  if (slots.length === 0) {
    return (grossCents * NET_REVENUE_MULTIPLIER * basePercent) / 100;
  }
  if (halfHours != null && bucketsRecollent(halfHours, grossCents)) {
    return commissionFromHalfHourBuckets(halfHours, basePercent, slots);
  }
  if (halfHours == null && grossCents === 0) return 0;
  return null;
}
